"use client";
import React, { useEffect, useRef } from "react";
import ModernNavBar from "@/components/ModernNavBar";
import Footer from "@/components/Footer";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useServerActionRecovery } from "@/hooks/useServerActionRecovery";
import { useAuth } from "@/contexts/AuthContext";
import { usePathname } from "@/navigation";
import { useUnseenNotificationCount } from "@/contexts/UnseenNotificationCountContext";
import type { NavVenue } from "@/server-side/data/venue";

export default function ClientLayout({
  children,
  initialVenues = null,
}: {
  children: React.ReactNode;
  // Resolved by the locale layout so the nav bar's venue-scoped items are
  // there on first paint. Null when the server could not resolve them.
  initialVenues?: NavVenue[] | null;
}) {
  // Sync auth context with error handler
  useErrorHandler();
  // Recover gracefully from server action version skew after a new deployment
  useServerActionRecovery();
  const { isAuthenticated } = useAuth();
  const pathname = usePathname();
  const { unseenCount } = useUnseenNotificationCount();

  // Update document title with unseen notification count
  const unseenCountRef = useRef(unseenCount);
  useEffect(() => {
    unseenCountRef.current = unseenCount;
    const baseTitle = document.title.replace(/^\(\d+\)\s*/, "");
    document.title = unseenCount > 0 ? `(${unseenCount}) ${baseTitle}` : baseTitle;
  }, [unseenCount]);

  // Re-apply count prefix whenever Next.js or navigation changes the title
  useEffect(() => {
    const applyTitlePrefix = () => {
      const count = unseenCountRef.current;
      const baseTitle = document.title.replace(/^\(\d+\)\s*/, "");
      const newTitle = count > 0 ? `(${count}) ${baseTitle}` : baseTitle;
      if (document.title !== newTitle) {
        document.title = newTitle;
      }
    };

    const observer = new MutationObserver(applyTitlePrefix);
    observer.observe(document.head, { childList: true, subtree: true, characterData: true });

    return () => observer.disconnect();
  }, []);
  
  // Check if we should show bottom navigation
  const isLandingPage = pathname === "/";
  const isDemoPage = pathname.startsWith("/demo/");
  const isJoinPage = (pathname as string) === "/staff/join";
  const hasBottomNavigation = !isLandingPage && isAuthenticated();

  if (isDemoPage || isJoinPage) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-900 to-gray-800">
      <ModernNavBar initialVenues={initialVenues} />
      <main
        className={`flex-grow ${
          hasBottomNavigation
            ? 'pb-[calc(80px+env(safe-area-inset-bottom,0px))] md:pb-0'
            : ''
        }`}
      >
        {children}
      </main>
      <Footer />
    </div>
  );
}